/**
 * Active climate target resolution for dashboard / climate UI.
 * Reuses shared SSOT helpers from shared/types/ClimateTargets.ts.
 */

import type { Rule } from '../hooks/useFirebaseData';
import type { Room } from '../hooks/useFirebaseData';
import {
  computeDehuOffThreshold,
  computeDehuStageOnThreshold,
  resolveDehuDeadband,
  type BatchScheduleAutomationConfig,
} from '../../shared/types/BatchSchedule';
import {
  getDefaultTargets,
  resolveActiveTargets,
  type ClimateTargets,
  type RoomClimateTargets,
  type TargetRange,
} from '../../shared/types/ClimateTargets';

export type ClimatePeriod = 'day' | 'night';
export type TargetSource = 'room' | 'legacy' | 'default';

export interface ResolveActiveClimateOptions {
  period?: ClimatePeriod;
  lightState?: ClimatePeriod;
  category?: string;
  roomType?: string;
}

export interface ResolvedActiveClimate {
  period: ClimatePeriod;
  targets: ClimateTargets;
  source: TargetSource;
}

/** Partial room climate as stored in Firestore (may be legacy flat or day/night). */
export type PartialRoomClimate = Partial<RoomClimateTargets> & {
  day?: Partial<ClimateTargets>;
  night?: Partial<ClimateTargets>;
  temperature?: Partial<TargetRange>;
  humidity?: Partial<TargetRange>;
  vpd?: Partial<TargetRange>;
  co2?: Partial<TargetRange>;
};

function isCompleteTargetRange(range: Partial<TargetRange> | undefined): range is TargetRange {
  return (
    typeof range?.min === 'number'
    && typeof range?.optimal === 'number'
    && typeof range?.max === 'number'
  );
}

function mergeMetric(
  primary: Partial<TargetRange> | undefined,
  fallback: TargetRange,
): TargetRange {
  if (isCompleteTargetRange(primary)) return primary;
  return fallback;
}

function mergeClimateTargets(
  primary: Partial<ClimateTargets> | undefined,
  fallback: ClimateTargets,
): ClimateTargets {
  return {
    temperature: mergeMetric(primary?.temperature, fallback.temperature),
    humidity: mergeMetric(primary?.humidity, fallback.humidity),
    vpd: mergeMetric(primary?.vpd, fallback.vpd),
    co2: mergeMetric(primary?.co2, fallback.co2),
  };
}

function hasDayNightStructure(climate: PartialRoomClimate): boolean {
  return Boolean(climate.day || climate.night);
}

function toRoomClimateTargets(climate: PartialRoomClimate): RoomClimateTargets {
  const defaults = getDefaultTargets();
  const day = mergeClimateTargets(climate.day, defaults.day);
  const night = mergeClimateTargets(climate.night, defaults.night);
  return {
    day,
    night,
    lightPercent: climate.lightPercent,
    lightHours: climate.lightHours,
    updatedAt: climate.updatedAt,
    temperature: climate.temperature as TargetRange | undefined ?? day.temperature,
    humidity: climate.humidity as TargetRange | undefined ?? day.humidity,
    vpd: climate.vpd as TargetRange | undefined ?? day.vpd,
    co2: climate.co2 as TargetRange | undefined ?? day.co2,
  };
}

export function resolveActivePeriod(
  period?: ClimatePeriod,
  lightState?: ClimatePeriod,
): ClimatePeriod {
  if (period === 'day' || period === 'night') return period;
  if (lightState === 'night') return 'night';
  return 'day';
}

export function resolveActiveRoomClimate(
  roomClimate: PartialRoomClimate | undefined,
  options: ResolveActiveClimateOptions = {},
): ResolvedActiveClimate {
  const period = resolveActivePeriod(options.period, options.lightState);
  const defaults = getDefaultTargets(options.category, options.roomType);
  const periodDefaults = period === 'night' ? defaults.night : defaults.day;

  if (!roomClimate) {
    return { period, targets: periodDefaults, source: 'default' };
  }

  if (hasDayNightStructure(roomClimate)) {
    const normalized = toRoomClimateTargets(roomClimate);
    return {
      period,
      targets: resolveActiveTargets(normalized, period),
      source: 'room',
    };
  }

  const legacy = mergeClimateTargets(roomClimate as Partial<ClimateTargets>, periodDefaults);
  const hasLegacy =
    isCompleteTargetRange(roomClimate.temperature)
    || isCompleteTargetRange(roomClimate.humidity)
    || isCompleteTargetRange(roomClimate.vpd)
    || isCompleteTargetRange(roomClimate.co2);

  if (hasLegacy) {
    return { period, targets: legacy, source: 'legacy' };
  }

  return { period, targets: periodDefaults, source: 'default' };
}

// ---------------------------------------------------------------------------
// Dehumidification policy display (v2 managed rules only on dashboard)
// ---------------------------------------------------------------------------

export type DehuPolicyStatus = 'v2' | 'legacy' | 'mixed' | 'none';

export interface DehuRoomPolicyV2 {
  version: 2;
  setpoint: number;
  holdBandLow: number;
  holdBandHigh: number;
  stageOnThresholds: number[];
  stageOffThresholds: number[];
  emergencyRh: number;
  maxActiveStages: number;
  minOnSeconds: number;
  minOffSeconds: number;
  period: ClimatePeriod;
  source: 'managed_rule';
}

export interface DehuRoomPolicyLegacy {
  version: 1;
  offThreshold: number;
  onThresholds: number[];
  period: ClimatePeriod;
  source: 'managed_rule' | 'automation_config';
}

export type DehuRoomPolicy = DehuRoomPolicyV2 | DehuRoomPolicyLegacy;

export function isManagedDehuRule(rule: Rule): boolean {
  return (
    rule.managedPurpose === 'dehumidifier_cascade'
    && rule.dehuControlMode === 'staged_target'
  );
}

export function groupManagedDehuRulesByRoom(rules: Rule[]): Map<string, Rule[]> {
  const map = new Map<string, Rule[]>();
  for (const rule of rules) {
    if (!rule.roomId || !isManagedDehuRule(rule)) continue;
    const list = map.get(rule.roomId) ?? [];
    list.push(rule);
    map.set(rule.roomId, list);
  }
  for (const [, list] of map) {
    list.sort((a, b) => (a.cascadeStage ?? 0) - (b.cascadeStage ?? 0));
  }
  return map;
}

export function classifyManagedDehuVersion(rules: Rule[]): DehuPolicyStatus {
  const managed = rules.filter(isManagedDehuRule);
  if (managed.length === 0) return 'none';
  const versions = new Set(managed.map(r => r.dehuControlVersion === 2 ? 2 : 1));
  if (versions.size > 1) return 'mixed';
  return versions.has(2) ? 'v2' : 'legacy';
}

function resolveV2PolicyFromRules(rules: Rule[], period: ClimatePeriod): DehuRoomPolicyV2 | null {
  const sorted = [...rules].sort((a, b) => (a.cascadeStage ?? 0) - (b.cascadeStage ?? 0));
  const first = sorted[0];
  if (!first || first.dehuControlVersion !== 2) return null;

  const isNight = period === 'night';
  const setpoint = isNight && typeof first.nightDehuSetpointRh === 'number'
    ? first.nightDehuSetpointRh
    : first.dehuSetpointRh;
  const holdLow = isNight && typeof first.nightDehuLeadOffRh === 'number'
    ? first.nightDehuLeadOffRh
    : first.dehuLeadOffRh;
  const holdHigh = isNight && typeof first.nightDehuLeadOnRh === 'number'
    ? first.nightDehuLeadOnRh
    : first.dehuLeadOnRh;

  if (typeof setpoint !== 'number' || typeof holdLow !== 'number' || typeof holdHigh !== 'number') {
    return null;
  }

  const stageOnThresholds: number[] = [];
  const stageOffThresholds: number[] = [];
  for (const rule of sorted) {
    const onRh = isNight && typeof rule.nightDehuStageOnRh === 'number'
      ? rule.nightDehuStageOnRh
      : rule.dehuStageOnRh;
    const offRh = isNight && typeof rule.nightDehuStageOffRh === 'number'
      ? rule.nightDehuStageOffRh
      : rule.dehuStageOffRh;
    if (typeof onRh === 'number') stageOnThresholds.push(onRh);
    if (typeof offRh === 'number') stageOffThresholds.push(offRh);
  }

  return {
    version: 2,
    setpoint,
    holdBandLow: holdLow,
    holdBandHigh: holdHigh,
    stageOnThresholds,
    stageOffThresholds,
    emergencyRh: (isNight && typeof first.nightDehuEmergencyRh === 'number'
      ? first.nightDehuEmergencyRh
      : first.dehuEmergencyRh) ?? setpoint + (first.dehuEmergencyMarginRh ?? 5),
    maxActiveStages: (isNight && typeof first.nightDehuMaxActiveStages === 'number'
      ? first.nightDehuMaxActiveStages
      : first.dehuMaxActiveStages) ?? stageOnThresholds.length,
    minOnSeconds: first.dehuMinOnSeconds ?? 300,
    minOffSeconds: first.dehuMinOffSeconds ?? 300,
    period,
    source: 'managed_rule',
  };
}

function resolveDehuPolicyFromAutomationConfig(
  config: BatchScheduleAutomationConfig,
  humidityTarget: number,
): DehuRoomPolicyLegacy | null {
  const stages = (config.dehuStages ?? []).filter(stage => stage.enabled);
  if (stages.length === 0) return null;

  const minDeadband = config.dehuMinDeadband ?? 3;
  const deadband = resolveDehuDeadband(config.dehuDeadband, minDeadband);
  const onThresholds = stages.map(stage =>
    computeDehuStageOnThreshold(humidityTarget, stage.offset),
  );

  return {
    version: 1,
    offThreshold: computeDehuOffThreshold(humidityTarget, deadband, minDeadband),
    onThresholds,
    period: 'day',
    source: 'automation_config',
  };
}

export function resolveDehuRoomPolicy(
  managedRules: Rule[] | undefined,
  room: Pick<Room, 'automationConfig' | 'climate' | 'category' | 'roomType' | 'lightState'>,
  period?: ClimatePeriod,
): DehuRoomPolicy | null {
  const activePeriod = resolveActivePeriod(period, room.lightState);
  const sortedRules = [...(managedRules ?? [])]
    .filter(isManagedDehuRule)
    .sort((a, b) => (a.cascadeStage ?? 0) - (b.cascadeStage ?? 0));

  const versionStatus = classifyManagedDehuVersion(sortedRules);
  if (versionStatus === 'v2') {
    return resolveV2PolicyFromRules(sortedRules, activePeriod);
  }
  if (versionStatus === 'mixed' || versionStatus === 'legacy') {
    return null;
  }

  const config = room.automationConfig;
  if (!config) return null;

  const { targets } = resolveActiveRoomClimate(room.climate as PartialRoomClimate, {
    period: activePeriod,
    lightState: room.lightState,
    category: room.category,
    roomType: room.roomType,
  });

  const policy = resolveDehuPolicyFromAutomationConfig(config, targets.humidity.optimal);
  if (!policy) return null;
  return { ...policy, period: activePeriod };
}

export function formatDehuDashboardPolicy(policy: DehuRoomPolicy | null): string | null {
  if (!policy) return null;
  if (policy.version === 2) {
    const hold = `${Math.round(policy.holdBandLow)}–${Math.round(policy.holdBandHigh)}`;
    const add = policy.stageOnThresholds.map(v => Math.round(v)).join('/');
    const release = policy.stageOffThresholds.map(v => Math.round(v)).join('/');
    return `RH ${Math.round(policy.setpoint)}% · hold ${hold}% · add ${add}% · release ${release}%`;
  }
  const off = `${Math.round(policy.offThreshold)}%`;
  const on = policy.onThresholds.map(value => Math.round(value)).join('/');
  return `Dehu ON ${on}% · OFF ${off}`;
}

export function formatDehuLegacyWarning(rules: Rule[] | undefined): string | null {
  const status = classifyManagedDehuVersion(rules ?? []);
  if (status === 'legacy' || status === 'mixed') {
    return 'Legacy managed dehu rules; regenerate with v2 migration to view target-band policy';
  }
  return null;
}
